/**
 * Custom-domain configuration (CICD1).
 *
 * The "easy upgrade for scale" deploy pillar made usable: a paid user attaches a
 * custom domain to their project's production instance through the REAL
 * `/projects/:id/domains` control-plane API (create → DNS instructions → verify →
 * delete), while a free-tier user sees an honest, labelled upgrade card instead
 * of a silently-dead input.
 *
 * The gate is the `maxCustomDomains` tier limit, passed in from the parent
 * (which reads it from `GET /account/usage`, the same rollup the Billing page and
 * UsageNudge use) — never a client guess. `null` = still loading the limit, `0` =
 * locked (anonymous/free), `> 0` = open. The server independently enforces the
 * same cap (`CustomDomain.create` → 403), so the client gate is defence-in-depth
 * and a UX affordance, not the only guard.
 *
 * All colour comes from `getClassMap()` theme tokens (never hardcoded hex), so it
 * stays coherent with the DeployPanel it mounts into.
 *
 * DNS verification is NOT the finish line (D3). A verified domain only serves
 * once the platform edge has issued its Let's Encrypt certificate, which happens
 * asynchronously and takes up to a couple of minutes — so every row reports
 * `certStatus` ({@link CertStatus}) rather than calling a verified hostname live.
 * The panel polls while a certificate is `pending` so the row reaches "Live" on
 * its own, and "Live" is the only wording that ever appears for a domain a
 * browser can actually load.
 *
 * @module
 */

import { useCallback, useEffect, useState } from 'react'

import { useHttpClient, useTranslation } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'
import { Button, Icon } from '@molecule/app-ui-react'

import { useIsMobile, useMediaQuery } from '../hooks/useMediaQuery.js'
import { emitNotification } from '../notifications.js'

/** A single DNS record the user must publish at their registrar. */
interface DnsRecord {
  name: string
  value: string
}

/** CNAME (routing) + TXT (ownership) verification records for a domain. */
interface DomainVerification {
  cname: DnsRecord
  txt: DnsRecord
}

/**
 * Edge TLS certificate state for a verified domain (D3). Issuance happens on the
 * platform edge and is ASYNCHRONOUS — up to a couple of minutes — so a domain
 * can be DNS-verified and still not serve a request.
 *
 * - `pending` — requested, not issued yet. The domain is NOT live.
 * - `issued` — the edge holds a certificate; the domain serves over HTTPS.
 * - `failed` — issuance failed; `certDetail` carries the reason.
 * - `unsupported` — this deployment has no certificate issuance configured.
 */
type CertStatus = 'pending' | 'issued' | 'failed' | 'unsupported'

/** A custom-domain row as returned by `GET /projects/:id/domains`. */
interface CustomDomainRow {
  id: string
  domain: string
  status: 'pending' | 'verified' | 'failed'
  verifyToken: string
  verifiedAt?: string | null
  verification?: DomainVerification
  /**
   * `null`/absent = no certificate has ever been requested, which is the shape
   * of a domain that is not verified yet (and of any row written before edge
   * issuance shipped). DNS `verified` alone is never rendered as "live".
   */
  certStatus?: CertStatus | null
  /** Why issuance failed, straight from the edge. */
  certDetail?: string | null
  certRequestedAt?: string | null
  certIssuedAt?: string | null
}

interface DomainConfigProps {
  /** False for read-only viewers: add/verify/delete are hidden (server-gated anyway). */
  canEdit?: boolean
  projectId: string
  /**
   * The owner's effective `maxCustomDomains` tier limit: `null` while the limit
   * is still loading, `0` for the locked (anonymous/free) state, `> 0` when the
   * add UI is available.
   */
  maxCustomDomains: number | null
}

/** English fallbacks for each domain status badge (rendered through `t()`). */
const STATUS_DEFAULT_LABELS: Record<CustomDomainRow['status'], string> = {
  pending: 'Pending DNS',
  verified: 'Connected',
  failed: 'Not found',
}

/**
 * Custom-domain section for the DeployPanel. Gated on the `maxCustomDomains`
 * tier limit (CICD1).
 *
 * @param props - The owning project id and the resolved tier limit.
 * @returns The custom-domain configuration UI.
 */
export function DomainConfig({ canEdit, projectId, maxCustomDomains }: DomainConfigProps) {
  const cm = getClassMap()
  const { t } = useTranslation()
  const http = useHttpClient()
  // 16px input font on phones/touch — iOS zooms any focusable field below 16px.
  const isMobile = useIsMobile()
  const coarse = useMediaQuery('(pointer: coarse)')

  const [domains, setDomains] = useState<CustomDomainRow[]>([])
  const [newDomain, setNewDomain] = useState('')
  const [adding, setAdding] = useState(false)
  const [addError, setAddError] = useState<string | null>(null)
  const [verifyingId, setVerifyingId] = useState<string | null>(null)

  const isLocked = maxCustomDomains === 0
  const isOpen = typeof maxCustomDomains === 'number' && maxCustomDomains > 0

  // Status → semantic theme-token text class. The badge tint is derived from this
  // colour via color-mix(currentColor), so no hex is hardcoded.
  const statusColorClass: Record<CustomDomainRow['status'], string> = {
    pending: cm.textWarning,
    verified: cm.textSuccess,
    failed: cm.textError,
  }

  // Certificate state → its own badge + colour (D3). A verified domain reads as
  // "Live" ONLY once the edge holds its certificate; until then the badge names
  // what is actually happening, because a green "Connected" on a hostname whose
  // TLS handshake fails is the bug this replaced.
  const certBadge: Record<CertStatus, { key: string; defaultLabel: string; color: string }> = {
    pending: {
      key: 'domain.status.certPending',
      defaultLabel: 'Issuing certificate',
      color: cm.textWarning,
    },
    issued: { key: 'domain.status.live', defaultLabel: 'Live', color: cm.textSuccess },
    failed: {
      key: 'domain.status.certFailed',
      defaultLabel: 'Certificate failed',
      color: cm.textError,
    },
    unsupported: {
      key: 'domain.status.certUnsupported',
      defaultLabel: 'Verified',
      color: cm.textMuted,
    },
  }

  /**
   * The one sentence a certificate state gets. Shared by the row and the
   * post-verify notification so the two can never contradict each other.
   *
   * @param row - The domain row to describe.
   * @returns The sentence, or `null` when no certificate has been requested.
   */
  const certSentence = (row: Pick<CustomDomainRow, 'certStatus' | 'certDetail'>): string | null => {
    switch (row.certStatus) {
      case 'pending':
        return t('domain.certPending', undefined, {
          defaultValue: 'Domain verified. Issuing the certificate — this takes a minute.',
        })
      case 'issued':
        return t('domain.certIssued', undefined, {
          defaultValue: 'Certificate issued — this domain serves over HTTPS.',
        })
      case 'failed':
        return row.certDetail
          ? t(
              'domain.certFailedDetail',
              { detail: row.certDetail },
              { defaultValue: `Certificate could not be issued: ${row.certDetail}` },
            )
          : t('domain.certFailed', undefined, {
              defaultValue: 'Certificate could not be issued.',
            })
      case 'unsupported':
        return t('domain.certUnsupported', undefined, {
          defaultValue:
            'Certificates are not configured on this deployment, so this domain is not serving HTTPS.',
        })
      default:
        return null
    }
  }

  const loadDomains = useCallback(async () => {
    try {
      const res = await http.get<{ domains: CustomDomainRow[] }>(`/projects/${projectId}/domains`)
      setDomains(res.data.domains ?? [])
    } catch (error) {
      // Best-effort: a list failure leaves the section empty rather than blocking
      // the rest of the panel. The add/verify actions surface their own errors.
      console.error('Failed to load custom domains', error)
    }
  }, [projectId, http])

  // Only fetch the list once the gate is open — a locked (free) user has none and
  // the endpoint isn't relevant to the upsell.
  useEffect(() => {
    if (isOpen) loadDomains()
  }, [isOpen, loadDomains])

  // Certificate issuance is asynchronous on the edge (D3): a row lands as
  // `pending` and flips to `issued` a minute or two later, with nothing to click
  // in between. Poll while any row is waiting so the panel reaches "Live" on its
  // own — re-clicking Verify to find out is not a thing we ask of the user. The
  // list route re-reads the edge for us (throttled to one call per domain per
  // 15s), so the cadence matches that floor: polling faster only re-serves the
  // same row. The poll stops the moment nothing is pending.
  const hasPendingCert = domains.some((d) => d.certStatus === 'pending')
  useEffect(() => {
    if (!isOpen || !hasPendingCert) return
    const timer = setInterval(loadDomains, 15000)
    return () => clearInterval(timer)
  }, [isOpen, hasPendingCert, loadDomains])

  const handleAdd = useCallback(async () => {
    const domain = newDomain.trim().toLowerCase()
    if (!domain || adding) return
    setAdding(true)
    setAddError(null)
    try {
      const res = await http.post<{ domain: CustomDomainRow; verification: DomainVerification }>(
        `/projects/${projectId}/domains`,
        { domain },
      )
      setDomains((prev) => [...prev, { ...res.data.domain, verification: res.data.verification }])
      setNewDomain('')
    } catch (error) {
      const data = (error as { response?: { data?: { error?: string } } }).response?.data
      setAddError(
        data?.error ??
          t('domain.addFailed', undefined, {
            defaultValue: 'Could not add that domain. Please try again.',
          }),
      )
    }
    setAdding(false)
  }, [newDomain, adding, projectId, http, t])

  const handleVerify = useCallback(
    async (id: string) => {
      if (verifyingId) return
      setVerifyingId(id)
      try {
        const res = await http.post<{ domain: CustomDomainRow; verified: boolean }>(
          `/projects/${projectId}/domains/${id}/verify`,
          {},
        )
        setDomains((prev) => prev.map((d) => (d.id === id ? { ...d, ...res.data.domain } : d)))
        if (res.data.verified) {
          // DNS verification does NOT make a domain live — the edge certificate
          // does, and it is issued asynchronously. Say whichever of the two just
          // happened, from the same sentence the row shows.
          const sentence = certSentence(res.data.domain)
          emitNotification(
            sentence ?? t('domain.verifiedNoCert', undefined, { defaultValue: 'Domain verified.' }),
            res.data.domain.certStatus === 'failed' ? 'error' : 'info',
          )
        }
      } catch (error) {
        // A 422 means the DNS records aren't visible yet — flip the row to
        // `failed` from the response body and tell the user to retry, don't throw.
        const row = (error as { response?: { data?: { domain?: CustomDomainRow } } }).response?.data
          ?.domain
        if (row) setDomains((prev) => prev.map((d) => (d.id === id ? { ...d, ...row } : d)))
        emitNotification(
          t('domain.verifyPending', undefined, {
            defaultValue:
              'DNS records not found yet. They can take a while to propagate — add the records and try again.',
          }),
          'warning',
        )
      }
      setVerifyingId(null)
    },
    // `certSentence` closes over `t` and is rebuilt each render; it is listed so
    // the notification can never fall behind the row's own wording.
    [verifyingId, projectId, http, t, certSentence],
  )

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        await http.delete(`/projects/${projectId}/domains/${id}`)
        setDomains((prev) => prev.filter((d) => d.id !== id))
      } catch (error) {
        console.error('Failed to delete custom domain', error)
        emitNotification(
          t('domain.deleteFailed', undefined, { defaultValue: 'Could not remove that domain.' }),
          'error',
        )
      }
    },
    [projectId, http, t],
  )

  return (
    <section data-mol-id="domain-section">
      <h3 className={cm.sectionHeading} style={{ margin: '0 0 8px' }}>
        {t('domain.title', undefined, { defaultValue: 'Custom Domain' })}
      </h3>

      {/* ── Locked (free / anonymous) — honest, labelled upsell ──────────────── */}
      {isLocked && (
        <div
          data-mol-id="domain-locked"
          className={cm.cn(cm.surface, cm.borderAll)}
          style={{ padding: '14px 16px', borderRadius: 8 }}
        >
          <div
            className={cm.cn(cm.textMuted, cm.fontWeight('medium'))}
            style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}
          >
            <Icon name="lock" size={14} aria-hidden="true" />
            {t('domain.lockedTitle', undefined, {
              defaultValue: 'Custom domains are a paid feature',
            })}
          </div>
          <p
            className={cm.textSubtle}
            style={{ margin: '8px 0 12px', fontSize: 13, lineHeight: 1.5 }}
          >
            {t('domain.lockedDesc', undefined, {
              defaultValue:
                'On the free plan your app stays live at its molecule.dev subdomain. Upgrade to the Pro or Team plan to point your own domain at it — and switch back instantly, the Molecule way.',
            })}
          </p>
          <a
            href="/pricing"
            data-mol-id="domain-upgrade-cta"
            className={cm.cn(cm.textPrimary, cm.link, cm.fontWeight('semibold'))}
            style={{ fontSize: 13, textDecoration: 'underline' }}
          >
            {t('upgrade.viewPlans', undefined, { defaultValue: 'View plans' })}
          </a>
        </div>
      )}

      {/* ── Loading the gate ─────────────────────────────────────────────────── */}
      {maxCustomDomains === null && (
        <p className={cm.textSubtle} style={{ margin: 0, fontSize: 13 }}>
          {t('common.loading', undefined, { defaultValue: 'Loading…' })}
        </p>
      )}

      {/* ── Open (paid) — add + manage domains ───────────────────────────────── */}
      {isOpen && (
        <>
          {/* Compact: the heading + the field's placeholder + the per-row DNS
              instructions carry the guidance, so no standalone description
              paragraph here (it was the section's biggest vertical cost).
              Wraps only when the row can't fit (phones): the input takes its own
              full-width line and the button drops below. */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <input
              type="text"
              data-mol-id="domain-add-input"
              value={newDomain}
              disabled={canEdit === false}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder={t('domain.placeholder', undefined, {
                defaultValue: 'app.example.com',
              })}
              style={{
                flex: '1 1 200px',
                minWidth: 0,
                height: isMobile ? 40 : 32,
                boxSizing: 'border-box',
                padding: '6px 10px',
                // 16px on phones/touch — iOS zooms any focusable field below 16px.
                fontSize: isMobile || coarse ? 16 : 13,
                borderRadius: 6,
                border: '1px solid var(--color-border-secondary)',
                background: 'var(--color-surface)',
                color: 'inherit',
                outline: 'none',
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAdd()
              }}
            />
            {canEdit !== false && (
              <Button
                size="sm"
                data-mol-id="domain-add-submit"
                onClick={handleAdd}
                disabled={adding || !newDomain.trim()}
                loading={adding}
                loadingText={t('domain.adding', undefined, { defaultValue: 'Adding…' })}
                style={{ height: isMobile ? 40 : 32 }}
              >
                {t('domain.add', undefined, { defaultValue: 'Add domain' })}
              </Button>
            )}
          </div>

          {addError && (
            <p
              data-mol-id="domain-add-error"
              role="alert"
              className={cm.textError}
              style={{ margin: '0 0 12px', fontSize: 12 }}
            >
              {addError}
            </p>
          )}

          {domains.length === 0 ? (
            <p className={cm.textSubtle} style={{ margin: '8px 0 0', fontSize: 13 }}>
              {t('domain.none', undefined, { defaultValue: 'No custom domains yet.' })}
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
              {domains.map((d) => {
                // A verified domain's badge is the CERTIFICATE's state, not the
                // DNS check's: only `issued` means a browser can reach it.
                const cert = d.status === 'verified' ? (d.certStatus ?? null) : null
                const badge = cert ? certBadge[cert] : null
                const colorClass = badge?.color ?? statusColorClass[d.status] ?? cm.textMuted
                const label = badge
                  ? t(badge.key, undefined, { defaultValue: badge.defaultLabel })
                  : t(`domain.status.${d.status}`, undefined, {
                      defaultValue: STATUS_DEFAULT_LABELS[d.status] ?? d.status,
                    })
                const isLive = cert === 'issued'
                // Scoped to verified rows on purpose: a stale `issued` beside a
                // DNS badge that just went back to "Not found" would claim the
                // domain serves when its records no longer point here.
                const certNote = cert ? certSentence(d) : null
                return (
                  <div
                    key={d.id}
                    data-mol-id={`domain-row-${d.id}`}
                    className={cm.borderAll}
                    style={{ borderRadius: 8, padding: '10px 12px' }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        // Wraps only when the domain + actions pinch (390px
                        // phones) — the buttons then drop to their own line.
                        flexWrap: 'wrap',
                        gap: 8,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <span
                          className={cm.cn(colorClass, cm.fontWeight('medium'))}
                          style={{
                            fontSize: 11,
                            padding: '2px 8px',
                            borderRadius: 100,
                            whiteSpace: 'nowrap',
                            background: 'color-mix(in srgb, currentColor 15%, transparent)',
                          }}
                        >
                          {label}
                        </span>
                        {/* The hostname is a link only once the certificate is
                            issued — before that the link would hand the user a
                            failed TLS handshake. */}
                        {isLive ? (
                          <a
                            href={`https://${d.domain}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-mol-id={`domain-link-${d.id}`}
                            className={cm.cn(cm.textPrimary, cm.link, cm.fontWeight('medium'))}
                            style={{
                              fontSize: 13,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {d.domain}
                          </a>
                        ) : (
                          <span
                            className={cm.fontWeight('medium')}
                            style={{
                              fontSize: 13,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {d.domain}
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {/* Issuance failed: re-running verification re-requests the
                            certificate, so the retry the user can take is the same
                            call — just labelled for what it does here. */}
                        {cert === 'failed' && canEdit !== false && (
                          <Button
                            size="sm"
                            color="secondary"
                            data-mol-id={`domain-cert-retry-${d.id}`}
                            onClick={() => handleVerify(d.id)}
                            disabled={verifyingId === d.id}
                            loading={verifyingId === d.id}
                            loadingText={t('domain.certRetrying', undefined, {
                              defaultValue: 'Retrying…',
                            })}
                          >
                            {t('domain.certRetry', undefined, {
                              defaultValue: 'Retry certificate',
                            })}
                          </Button>
                        )}
                        {d.status !== 'verified' && canEdit !== false && (
                          <Button
                            size="sm"
                            color="secondary"
                            data-mol-id={`domain-verify-${d.id}`}
                            onClick={() => handleVerify(d.id)}
                            disabled={verifyingId === d.id}
                            loading={verifyingId === d.id}
                            loadingText={t('domain.verifying', undefined, {
                              defaultValue: 'Verifying…',
                            })}
                          >
                            {t('domain.verify', undefined, { defaultValue: 'Verify DNS' })}
                          </Button>
                        )}
                        {canEdit !== false && (
                          <Button
                            size="sm"
                            color="error"
                            variant="ghost"
                            data-mol-id={`domain-delete-${d.id}`}
                            onClick={() => handleDelete(d.id)}
                            aria-label={t('domain.remove', undefined, { defaultValue: 'Remove' })}
                          >
                            <Icon name="trash" size={14} aria-hidden="true" />
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Certificate state in one plain sentence (D3), on every
                        verified row that has one: "verified" and "serving" are
                        different facts and the user is owed the second. */}
                    {certNote && (
                      <p
                        data-mol-id={`domain-cert-${d.id}`}
                        className={cert ? certBadge[cert].color : cm.textSubtle}
                        style={{ margin: '8px 0 0', fontSize: 12, lineHeight: 1.5 }}
                      >
                        {certNote}
                      </p>
                    )}

                    {/* DNS records to publish (CNAME routes, TXT proves ownership).
                        Shown for any not-yet-verified domain so it's never a dead
                        end — the list endpoint carries them, not just create. */}
                    {d.status !== 'verified' && d.verification && (
                      <div
                        className={cm.borderT}
                        style={{
                          marginTop: 10,
                          paddingTop: 10,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 8,
                        }}
                      >
                        <p className={cm.textSubtle} style={{ margin: 0, fontSize: 12 }}>
                          {t('domain.dnsInstructionsBoth', undefined, {
                            defaultValue:
                              'Add both records at your domain registrar. The CNAME points your domain at your app. The TXT record proves you own it, and sits on its own name so it can live beside the CNAME. For a root domain that cannot take a CNAME, publish only the TXT record and use your registrar’s alias or flattened record for routing.',
                          })}
                        </p>
                        {(
                          [
                            { type: 'CNAME', record: d.verification.cname },
                            { type: 'TXT', record: d.verification.txt },
                          ] as const
                        ).map(({ type, record }) => (
                          <div
                            key={type}
                            data-mol-id={`domain-dns-${type.toLowerCase()}-${d.id}`}
                            className={cm.cn(cm.surface, cm.borderAll)}
                            style={{
                              display: 'flex',
                              gap: 8,
                              padding: '6px 10px',
                              borderRadius: 6,
                              fontSize: 11,
                              fontFamily: 'monospace',
                              flexWrap: 'wrap',
                            }}
                          >
                            <span className={cm.textPrimary} style={{ minWidth: 48 }}>
                              {type}
                            </span>
                            <span style={{ wordBreak: 'break-all' }}>{record.name}</span>
                            <span className={cm.textMuted} style={{ wordBreak: 'break-all' }}>
                              {record.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}
    </section>
  )
}
